import { Attachment } from 'src/app/interfaces/api/attachment';
import { Metric } from 'src/app/interfaces/api/metric';
import { Tag } from 'src/app/interfaces/shared/tag';

export interface RiskEntry {
  number: number;
  metric: Metric;
  box?: Tag;
}

/** Maps a risk title to its SVG icon name under public/models/risks/SVG/. */
export function iconNameForTitle(title: string | undefined): string {
  switch (title) {
    case "Chute de hauteur": return "hauteur";
    case "Levage Elingage": return "levage";
    case "Coactivité":
    case "Co-activité Engins Piétons": return "coactivite";
    case "Gestes et postures": return "posture";
    case "Chute de plain-pied": return "plain-pied";
    case "Basculement Renversement Effondrement": return "effondrement";
    case "Ensevelissement": return "ensevelissement";
    default: return "danger";
  }
}

export function isFaceTag(name: string | undefined): boolean {
  return name?.trim().toLowerCase() === "face";
}

export function isRisk(metric: Metric): boolean {
  return !isFaceTag(metric.payload?.title);
}

export interface RiskImage {
  attachmentId: string;
  attachment?: Attachment;
  risks: RiskEntry[];
}

/**
 * Group risk metrics by their image (attachment) so multiple risks can be shown per image.
 * Ordering within an image follows the attachment's confirmed `tags.risk[]` order, matched by
 * `box._id === metric.payload.tagId`; metrics without a matching box fall back to createdDate.
 * The 1-based `number` is the shared visual id of a risk (tab, photo tag, map icon, PDF pastille).
 */
export function groupRisksByImage(metrics: Metric[], attachmentsById?: Record<string, Attachment>): RiskImage[] {
  const groups = new Map<string, Metric[]>();

  for (const m of metrics || []) {
    const key = m.payload?.attachment || `__noatt__${m.id}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(m);
    else groups.set(key, [m]);
  }

  const images: RiskImage[] = [];
  for (const [attachmentId, riskMetrics] of groups) {
    const attachment = attachmentsById?.[attachmentId];
    const boxes = attachment?.tags?.risk ?? [];
    const ordered = orderRisks(riskMetrics, boxes);

    images.push({
      attachmentId,
      attachment,
      risks: ordered.map((metric, idx) => ({
        number: idx + 1,
        metric,
        box: findBox(metric, boxes),
      })),
    });
  }

  return images;
}

function findBox(metric: Metric, boxes: Tag[]): Tag | undefined {
  const tagId = metric.payload?.tagId;
  if (!tagId) return undefined;
  return boxes.find(b => b._id === tagId);
}

function orderRisks(metrics: Metric[], boxes: Tag[]): Metric[] {
  const confirmed = boxes.filter(b => b.probability === 1);

  const orderIndex = (m: Metric): number => {
    const tagId = m.payload?.tagId;
    if (tagId) {
      const idx = confirmed.findIndex(b => b._id === tagId);
      if (idx >= 0) return idx;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  return [...metrics].sort((a, b) => {
    const diff = orderIndex(a) - orderIndex(b);
    if (diff !== 0) return diff;
    return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
  });
}
